/**
 * @file BlendGrid.js
 * @version 3.8.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-13
 * @modified 2026-10-01
 * @description The inclusion grid: one row per material in the blend, the
 *              inclusion % typed into it, and everything that follows from that.
 *
 *              Row actions follow the rest of the app: a checkbox column for
 *              selection, a single three-dot RowActionsMenu per row, and a
 *              right-click TableContextMenu that acts on the whole selection
 *              when you right-click a selected row and on that row alone
 *              otherwise (the decision lives in useTableContextMenu, not here).
 *
 *              Deliberate deviation from the shared Input component: each cell
 *              is a raw <input>. Input swallows `step` and wraps its field in
 *              icon markup, neither of which survives being a table cell 40 rows
 *              deep. Everything else uses the shared vocabulary (.matrix-table
 *              sticky columns, TickCheckbox, useDragSelect, RowActionsMenu,
 *              TableContextMenu, SearchableDropdown).
 *
 *              Rows are ordered by the material master's sort_order rather than
 *              by insertion, so a blend always reads in the same order as the
 *              workbook people are coming from, and adding a material doesn't
 *              reshuffle the rows under the cursor.
 *
 *              The £/T header sorts by raw material cost per tonne: cheapest
 *              first, dearest first, then back to that workbook order. Unpriced
 *              materials sit at the bottom either way (utils/blendGridSort.js).
 *              Keyboard navigation follows what is on screen — the arrow keys
 *              walk the rows in the order they are displayed.
 *
 *              The (i) beside each material name shows its nutritional values.
 *              It is on EVERY known material, in the blend or not: with "show
 *              every material" on, a blank blend used to list forty materials
 *              and no icons at all, because the peek only rendered for rows
 *              already in the blend — which read as the feature having gone.
 *              The panel is drawn in a portal at a fixed position
 *              (useAnchoredTip), so neither the grid's scroll box nor a sticky
 *              cell can clip or cover it. Hover or focus shows it, a click or
 *              tap pins it, and "?" in a row's quantity cell toggles it without
 *              leaving the keyboard (Tab never reaches the icon — the grid keeps
 *              Tab for its own cells).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SearchableDropdown from './SearchableDropdown';
import TickCheckbox from './TickCheckbox';
import RowActionsMenu from './RowActionsMenu';
import { checkInclusion } from '../utils/blendLimits';
import TableContextMenu from './TableContextMenu';
import useGridNav from '../hooks/useGridNav';
import useDragSelect from '../hooks/useDragSelect';
import useTableContextMenu from '../hooks/useTableContextMenu';
import useStickyColumnOffsets from '../hooks/useStickyColumnOffsets';
import { useSlideOutButton } from './SlideOutButton';
import { formatCurrency } from '../utils/currencyFormatter';
import * as blendMath from '../utils/blendMath';
import { sortRowsByCost, nextSortDirection } from '../utils/blendGridSort';
import usePersistedState from '../hooks/usePersistedState';
import useAnchoredTip from '../hooks/useAnchoredTip';
// The same function the proportional line uses, so a row's left border and its
// section on the line are the same colour by construction rather than by two
// palettes being kept in step.
import { colourFor } from './BlendAllocationBar';

const QTY_COL = 'qty';

// Both keys work in any quantity cell (onKeyDown below). Since the grid tip
// modal went (c15ef01) this header is the only place either is written down.
const QTY_SHORTCUTS_HINT =
  'In a cell: = fills it with whatever takes the blend to 100% · ? shows that material\'s nutritional values';

// The (i) sits on the name's first line, so the panel is placed against the
// whole name block — wrapped name and NO PRICE line included — rather than the
// icon, or it would open over the material's own second line. The icon leads
// the block, so the left edge is the same either way.
const peekAnchorRect = (el) => (el.closest('.blend-material-cell') || el).getBoundingClientRect();

function fmt(value, decimals = 2) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return Number(value).toFixed(decimals);
}

export default function BlendGrid({
  materials = [],
  draft,
  summary,
  priceColumnKey,
  nutrientComponents = [],
  showAllMaterials = false,
  onToggleShowAll = null,
  onPasteNames = null,
  // Supplied so the row menu balances the same way the toolbar and the bar do.
  // Passing draft.balance bare would hand it the click event as its mode — it
  // falls through to proportional by luck rather than by intent.
  onBalance = null,
  // Comparing: B is the reference blend, held beside A. Its quantities and its
  // per-material cost contributions get their own read-only columns — A is the
  // blend being edited, and the only one that can be.
  compareValues = null,
  compareContributions = null,
  disabled = false
}) {
  const [addKey, setAddKey] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  // 'asc' | 'desc' | null (the workbook's order). A way of looking, so it
  // survives navigating away and back like the show-every-material toggle.
  const [costSort, setCostSort] = usePersistedState('blendGrid.costSort', null);

  // Enter past the last row jumps back to the add control (which sits at the
  // top of the grid). Found by ref rather than by position, so moving the row
  // doesn't break it. SearchableDropdown renders its search field only while
  // open, so this clicks the trigger rather than hunting for an input that
  // isn't in the DOM yet; the component focuses the field itself once open.
  const addWrapRef = useRef(null);
  const focusAddRow = useCallback(() => {
    const wrap = addWrapRef.current;
    if (!wrap) return;
    // The trigger TOGGLES, so clicking it while the menu is already open closes
    // it. That combination is reachable: Enter on the last row opens the picker,
    // and clicking + straight afterwards would shut it and drop focus on the
    // floor. Open means open — focus the field that's already there.
    const search = wrap.querySelector('.dropdown-search-input');
    if (search) { search.focus(); return; }
    wrap.querySelector('.dropdown-trigger')?.click();
  }, []);

  const byKey = useMemo(() => {
    const map = new Map();
    for (const m of materials) map.set(m.key, m);
    return map;
  }, [materials]);

  // Which materials get a row. Default is only what's in the blend; the "show
  // all" toggle reproduces the workbook's full fixed list for anyone who wants
  // to tab down it.
  const rows = useMemo(() => {
    // While comparing, a material in EITHER blend gets a row. A material A uses
    // and B does not is exactly what you opened the comparison to see, and
    // leaving it out of the grid hides the difference that matters most.
    const inBlend = new Set([
      ...Object.keys(draft.values),
      ...Object.keys(compareValues || {})
    ]);
    const list = materials.filter((m) => showAllMaterials || inBlend.has(m.key));

    // A material in the draft that the book has never heard of — a saved blend
    // whose material was later deactivated. Show it rather than silently
    // dropping the inclusion.
    const orphans = [...inBlend]
      .filter((k) => !byKey.has(k))
      .map((k) => ({ key: k, name: k, missing: true, sort_order: 99999 }));

    // Sorted HERE, before the row keys are taken, so the keyboard grid walks
    // the rows in the order they are drawn rather than the order they were in.
    return sortRowsByCost(
      [...list, ...orphans],
      (row) => (row.missing ? null : byKey.get(row.key)?.costs?.[priceColumnKey]),
      costSort
    );
  }, [materials, draft.values, compareValues, showAllMaterials, byKey, priceColumnKey, costSort]);

  const rowKeys = useMemo(() => rows.map((r) => r.key), [rows]);

  // Only rows actually in the blend can be selected — there is nothing to lock,
  // balance or remove on a row showing 0 because "show every material" is on.
  const selectableKeys = useMemo(
    () => rowKeys.filter((k) => draft.values[k] !== undefined),
    [rowKeys, draft.values]
  );

  // Drop selections for rows that have left the blend, so a bulk action can't
  // act on something no longer on screen.
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((k) => draft.values[k] !== undefined));
      return next.size === prev.size ? prev : next;
    });
  }, [draft.values]);

  const dragSelect = useDragSelect({
    setChecked: (key, checked) => setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key); else next.delete(key);
      return next;
    })
  });

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) =>
      prev.size === selectableKeys.length ? new Set() : new Set(selectableKeys));
  }, [selectableKeys]);

  const contributionsByKey = useMemo(() => {
    const map = new Map();
    const list = summary?.pricing?.[priceColumnKey]?.contributions || [];
    for (const c of list) map.set(c.key, c);
    return map;
  }, [summary, priceColumnKey]);

  const comparing = !!compareValues;

  /**
   * The sticky columns' `left` offsets, measured rather than written down.
   *
   * The material column sizes to its content, so nothing after it sits at a
   * width that can be known in advance — and the frozen block gains a column
   * when comparing, which moves the two after it.
   */
  const gridRef = useRef(null);
  useStickyColumnOffsets(gridRef, [comparing]);

  /** The reference blend's per-material cost, keyed for the Mat £ A column. */
  const compareByKey = useMemo(() => {
    const map = new Map();
    for (const c of compareContributions || []) map.set(c.key, c.contribution);
    return map;
  }, [compareContributions]);

  /** B's column totals, for the foot. Summed from what the rows show, so the
      column always adds up to itself. */
  const compareTotal = useMemo(() => Object.values(compareValues || {})
    .reduce((s, v) => s + (blendMath.toNumber(v) || 0), 0), [compareValues]);
  const compareCost = useMemo(() => (compareContributions || [])
    .reduce((s, c) => s + (c.contribution || 0), 0), [compareContributions]);

  const missingPrice = useMemo(
    () => new Set(summary?.pricing?.[priceColumnKey]?.missing || []),
    [summary, priceColumnKey]
  );

  const grid = useGridNav({
    rowKeys,
    colKeys: [QTY_COL],
    onLastRowEnter: focusAddRow
  });

  // One nutrition panel for the whole grid, keyed by the row it describes.
  const peek = useAnchoredTip({ anchorRect: peekAnchorRect });
  const peekMaterial = peek.openId !== null ? byKey.get(peek.openId) : null;
  const peekContribution = peekMaterial && draft.values[peek.openId] !== undefined
    ? contributionsByKey.get(peek.openId)
    : null;

  const total = blendMath.total(draft.values);
  const remaining = Math.round((100 - total) * 100) / 100;

  const handleAdd = useCallback((materialKey) => {
    if (!materialKey) return;
    setAddKey(null);
    if (draft.values[materialKey] === undefined) draft.addMaterial(materialKey, '');
    // Land in the new row's quantity cell so a recipe can be typed straight
    // through without reaching for the mouse.
    setTimeout(() => grid.focusCell(materialKey, QTY_COL), 0);
  }, [draft, grid]);

  /**
   * Paste from a spreadsheet.
   *
   *  - A single column of numbers fills downward from the focused cell.
   *  - Two columns (name, value) — what you get selecting the material and
   *    quantity columns out of the workbook — go to the caller to resolve names
   *    against the master list and its aliases.
   *
   * Never silently drops a line: unmatched names come back for mapping.
   */
  const handlePaste = useCallback((event) => {
    const text = event.clipboardData?.getData('text/plain');
    // A plain single value with no tabs or newlines isn't a grid paste — let it
    // land in the focused input the way any other paste would.
    if (!text || (!text.includes('\t') && !text.includes('\n'))) return;

    event.preventDefault();
    const matrix = text
      .split(/\r\n|\n|\r/)
      .filter((line) => line.trim() !== '')
      .map((line) => line.split('\t').map((cell) => cell.trim()));
    if (!matrix.length) return;

    const firstColNumeric = matrix.every((r) => r[0] === '' || Number.isFinite(Number(r[0])));

    if (firstColNumeric && matrix[0].length === 1) {
      // Values only — fill down from wherever the cursor is.
      const startIndex = Math.max(0, rowKeys.indexOf(grid.active.row));
      const next = { ...draft.values };
      let written = 0;
      matrix.forEach((cells, i) => {
        const key = rowKeys[startIndex + i];
        if (!key) return;
        next[key] = cells[0];
        written += 1;
      });
      draft.replaceValues(next);
      if (written < matrix.length && onPasteNames) {
        onPasteNames({ overflow: matrix.length - written });
      }
      return;
    }

    // Name + value. The caller resolves and confirms.
    if (onPasteNames) {
      onPasteNames({
        pairs: matrix
          .map((cells) => ({ name: cells[0], value: cells[cells.length - 1] }))
          .filter((p) => p.name)
      });
    }
  }, [draft, grid.active.row, rowKeys, onPasteNames]);

  const materialOptions = useMemo(() => materials
    .filter((m) => draft.values[m.key] === undefined)
    .map((m) => ({
      value: m.key,
      label: m.name,
      searchText: [m.name, m.supplier_code, ...(m.aliases || [])].filter(Boolean).join(' '),
      badge: m.category || undefined
    })), [materials, draft.values]);

  // ─── Actions ──────────────────────────────────────────────────────────────
  // One builder feeds both the three-dot menu and the right-click menu, so the
  // two can never drift apart. Falsy entries would crash RowActionsMenu, hence
  // the spread guards rather than `cond && {...}`.

  const buildRowActions = useCallback((row) => {
    const key = row.key;
    const inBlend = draft.values[key] !== undefined;
    const isLocked = draft.locked.includes(key);
    const name = row.missing ? key : (byKey.get(key)?.name || key);

    return [
      {
        key: 'lock',
        icon: isLocked ? 'lock_open' : 'lock',
        label: isLocked ? 'Unlock' : 'Lock',
        title: isLocked ? 'Allow balancing to move this' : "Balancing won't move this",
        disabled: !inBlend || disabled,
        onClick: () => draft.toggleLock(key)
      },
      {
        key: 'fill',
        icon: 'exposure',
        label: `Fill to ${remaining >= 0 ? '+' : ''}${remaining}%`,
        title: 'Set this material to whatever is missing from 100%',
        disabled: !inBlend || disabled,
        onClick: () => draft.fillToBalance(key)
      },
      {
        key: 'remove',
        icon: 'close',
        label: `Remove ${name}`,
        className: 'action-danger',
        disabled: !inBlend || disabled,
        onClick: () => draft.removeMaterial(key)
      }
    ];
  }, [draft, byKey, remaining, disabled]);

  const buildBulkActions = useCallback((ids) => {
    const keys = [...ids];
    const allLocked = keys.every((k) => draft.locked.includes(k));
    return [
      {
        key: 'bulk-lock',
        icon: allLocked ? 'lock_open' : 'lock',
        label: `${allLocked ? 'Unlock' : 'Lock'} ${keys.length} material${keys.length === 1 ? '' : 's'}`,
        onClick: () => {
          const locked = new Set(draft.locked);
          for (const k of keys) { if (allLocked) locked.delete(k); else locked.add(k); }
          draft.replaceValues(draft.values, [...locked]);
        }
      },
      {
        key: 'bulk-remove',
        icon: 'delete',
        label: `Remove ${keys.length} material${keys.length === 1 ? '' : 's'}`,
        onClick: () => {
          const next = { ...draft.values };
          for (const k of keys) delete next[k];
          draft.replaceValues(next, draft.locked.filter((k) => !ids.has(k)));
          setSelected(new Set());
        }
      },
      {
        key: 'bulk-zero',
        icon: 'exposure_zero',
        label: `Set ${keys.length} to zero`,
        onClick: () => {
          const next = { ...draft.values };
          for (const k of keys) next[k] = 0;
          draft.replaceValues(next);
        }
      },
      {
        key: 'clear-selection',
        icon: 'close',
        label: 'Clear selection',
        onClick: () => setSelected(new Set())
      }
    ];
  }, [draft]);

  const genericActions = useCallback(() => [
    {
      key: 'balance',
      icon: 'balance',
      label: 'Balance to 100%',
      disabled: Math.abs(total - 100) <= 0.01,
      onClick: () => (onBalance ? onBalance() : draft.balance())
    },
    ...(onToggleShowAll ? [{
      key: 'show-all',
      icon: 'list',
      label: 'Show every material',
      active: showAllMaterials,
      onClick: () => onToggleShowAll(!showAllMaterials)
    }] : []),
    {
      key: 'select-all',
      icon: 'select_all',
      label: selected.size === selectableKeys.length ? 'Deselect all' : 'Select all',
      disabled: !selectableKeys.length,
      onClick: toggleSelectAll
    }
  ], [draft, total, onToggleShowAll, showAllMaterials, selected.size,
      selectableKeys.length, toggleSelectAll, onBalance]);

  // The hook decides between "act on the selection" and "act on this row":
  // >1 selected AND the right-clicked row is one of them → bulk.
  const ctxMenu = useTableContextMenu({
    rows,
    selectedIds: selected,
    getRowId: (r) => r.key
  });

  const allSelected = selectableKeys.length > 0 && selected.size === selectableKeys.length;

  // The bulk bar mounts and unmounts with the selection, so its slide-out
  // buttons must be measured here rather than by the page — a hook in an
  // always-mounted parent runs before these exist and leaves them collapsed to
  // an icon with no label.
  /**
   * How much of the grid is off-screen, either side.
   *
   * The nutrient columns run well past the right edge and there is nothing at
   * that edge to say so — the four pinned columns look like the whole table
   * until you happen to scroll. The cue fades in only when there is something
   * to reach, and fades out at the end, so it reads as "more this way" rather
   * than as permanent chrome.
   */
  const scrollerRef = useRef(null);
  const [overflow, setOverflow] = useState({ left: false, right: false, hidden: 0 });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const max = el.scrollWidth - el.clientWidth;
      setOverflow({
        left: el.scrollLeft > 2,
        right: el.scrollLeft < max - 2,
        hidden: Math.max(0, max - el.scrollLeft)
      });
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    // Columns come and go with the nutrient picker, and the panel beside the
    // grid resizes as the window does; both change what is reachable.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [nutrientComponents, showAllMaterials]);

  const nudge = (direction) => {
    const el = scrollerRef.current;
    if (el) el.scrollBy({ left: direction * Math.max(160, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  useSlideOutButton([selected.size, draft.locked.length]);

  return (
    <div className="blend-grid-wrap" onPaste={handlePaste} {...grid.containerProps}>
      {selected.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <span className="bulk-actions-title">Bulk actions:</span>
            <span>{selected.size} material{selected.size === 1 ? '' : 's'} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            {buildBulkActions(selected).map((action) => (
              <div key={action.key}>
                <button
                  className={`btn-slideout${action.key === 'bulk-remove' ? ' btn-danger' : ''}`}
                  onClick={action.onClick}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">{action.icon}</span>
                  </div>
                  <span className="btn-label">{action.label}</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {overflow.right && (
        <button type="button" className="blend-scroll-cue" onClick={() => nudge(1)}
                title="More columns this way" aria-label="Scroll the grid right">
          <span className="material-symbols-rounded">chevron_right</span>
        </button>
      )}

      <div className={`table-container blend-scroller${overflow.right ? ' has-more-right' : ''}${overflow.left ? ' has-more-left' : ''}`}
           ref={scrollerRef}>
        {/* Anchored to the wrap rather than the scroller, so the cue stays put
            instead of riding along with the content it is pointing at. */}
        {/* `comparing` on the table, not just in the rows: it changes how many
            sticky columns there are, and the offsets differ accordingly. */}
        <table ref={gridRef}
               className={`matrix-table blend-grid${comparing ? ' comparing' : ''}`}
               onContextMenu={ctxMenu.onTableContextMenu}>
          <thead>
            <tr>
              <th className="sticky-col-1 checkbox-col">
                <TickCheckbox
                  id="blend-select-all"
                  checked={allSelected}
                  disabled={!selectableKeys.length}
                  onChange={toggleSelectAll}
                  ariaLabel="Select all materials in this blend"
                />
              </th>
              <th className="sticky-col-2">Material</th>
              {/* The grid's two shortcut keys, said where the cells are. Not
                  on the (i) itself: a native tooltip there would sit on top
                  of the panel it opens. */}
              <th className="sticky-col-3" title={QTY_SHORTCUTS_HINT}>
                Qty %{comparing && <span className="blend-ab-tag is-a">A</span>}
              </th>
              {comparing && (
                <th className="sticky-col-3 bg-ref-col">
                  Qty % <span className="blend-ab-tag is-b">B</span>
                </th>
              )}
              <th className="sticky-col-4 sticky-col-last actions-col">&nbsp;</th>
              {/* Same shape as every other sortable header (th.sortable, ▲/▼),
                  with a third click back to the workbook's order. */}
              <th className="first-scrollable bg-num sortable"
                  onClick={() => setCostSort(nextSortDirection(costSort))}
                  aria-sort={costSort === 'asc' ? 'ascending' : costSort === 'desc' ? 'descending' : 'none'}
                  title={costSort === 'asc'
                    ? 'Cheapest first — click for dearest first'
                    : costSort === 'desc'
                      ? 'Dearest first — click to return to the usual order'
                      : 'Sort by raw material cost per tonne, cheapest first'}>
                £/T{costSort === 'asc' ? ' ▲' : costSort === 'desc' ? ' ▼' : ''}
              </th>
              <th className="bg-num">
                Mat £{comparing && <span className="blend-ab-tag is-a">A</span>}
              </th>
              {comparing && (
                <th className="bg-num bg-ref-col">
                  Mat £ <span className="blend-ab-tag is-b">B</span>
                </th>
              )}

            </tr>
          </thead>
          <tbody>
            {/* Type-to-add lives in the grid rather than behind a button, so
                adding a material and typing its inclusion is one continuous
                keyboard gesture. It sits at the TOP: with a long material list
                a bottom row scrolls out of reach, and "where do I add one"
                should be the first thing the eye lands on, not the last.

                One cell across all four pinned columns, so the control is the
                full width of the frozen section rather than squeezed into the
                210px material column. */}
            <tr className="blend-add-row">
              {/* Five pinned columns while comparing, four otherwise — the
                  control spans the frozen block, whatever it currently is. */}
              <td className="blend-add-cell" colSpan={comparing ? 5 : 4}>
                {/* The plus is drawn inside the dropdown trigger in CSS rather
                    than sat beside it, so the whole 400px of pinned columns is
                    one clickable control instead of an icon with a control next
                    to it. */}
                <div className="blend-add-inner">
                  {/* The grid's scroll box is only as tall as its rows, so with
                      two materials in the blend there is nowhere for the picker
                      to open. It measures against the content panel instead and
                      leaves the scroll box in order to use that space. */}
                  <div ref={addWrapRef} className="blend-add-picker">
                    <SearchableDropdown
                      options={materialOptions}
                      value={addKey}
                      onChange={handleAdd}
                      disabled={disabled}
                      placeholder="Add material"
                      searchPlaceholder="Type a material name…"
                      boundarySelector=".search-content"
                      escapeOverflow
                    />
                  </div>
                </div>
              </td>
              {/* The hint that used to live here is gone. The gestures it
                  described are discoverable, and it cost a permanent row of
                  chrome to say so once. */}
              <td className="first-scrollable" />
              {comparing && <td />}
              {/* Show-every-material sits at the end of the add row rather than
                  up in the work bar: it decides what the grid LISTS, so it
                  belongs on the grid, next to the control that adds to it. */}
              <td className="blend-showall-cell">
                {onToggleShowAll && (
                  /* On/off is carried by the icon alone — swapping the eye and
                     colouring it. A button that also changes its border and
                     ground reads as a different control rather than the same
                     one in another state. */
                  <button type="button"
                          className={`btn-icon blend-showall${showAllMaterials ? ' is-on' : ''}`}
                          onClick={() => onToggleShowAll(!showAllMaterials)}
                          title={showAllMaterials
                            ? 'Showing every material — click to show only what is in the blend'
                            : 'Show every material, not just what is in the blend'}>
                    <span className="material-symbols-rounded">
                      {showAllMaterials ? 'visibility' : 'visibility_off'}
                    </span>
                  </button>
                )}
              </td>
            </tr>

            {rows.map((row) => {
              const material = byKey.get(row.key);
              const raw = draft.values[row.key];
              const inBlend = raw !== undefined;
              // Null, undefined, '' and 0 all mean "no section on the line".
              const hasQty = (blendMath.toNumber(raw) || 0) > 0;
              const isLocked = draft.locked.includes(row.key);
              const isSelected = selected.has(row.key);
              const contribution = contributionsByKey.get(row.key);
              const compareValue = compareValues ? compareValues[row.key] : undefined;
              const compareContribution = compareByKey.get(row.key);
              // A row recedes only when NEITHER side uses the material. While
              // comparing, one B has and A does not is the whole point of
              // looking — dimming it hides the difference being examined.
              const usedByCompare = comparing && (blendMath.toNumber(compareValue) || 0) > 0;
              const unused = !hasQty && !usedByCompare;
              const priceMissing = inBlend && missingPrice.has(row.key);
              // Only ever non-null for a material with a SIGNED-OFF limit.
              const inclusionCheck = checkInclusion(row.key, raw);
              const price = material?.costs?.[priceColumnKey];

              return (
                <tr
                  key={row.key}
                  onContextMenu={ctxMenu.onRowContextMenu(row)}
                  className={[
                    'blend-row',
                    unused ? 'not-in-blend' : 'in-blend',
                    isSelected ? 'selected' : '',
                    row.missing ? 'material-missing' : '',
                    priceMissing ? 'price-missing' : ''
                  ].filter(Boolean).join(' ')}
                >
                  {/* The colour key. Transparent unless the row carries a
                      quantity — a material sitting at 0 has no section on the
                      line, so colouring its border would point at nothing. */}
                  <td className="sticky-col-1 checkbox-col"
                      style={{ borderLeftColor: hasQty ? colourFor(row.key) : 'transparent' }}
                      onClick={(e) => e.stopPropagation()}>
                    <TickCheckbox
                      id={`blend-select-${row.key}`}
                      checked={isSelected}
                      disabled={!inBlend}
                      onChange={() => setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(row.key)) next.delete(row.key); else next.add(row.key);
                        return next;
                      })}
                      onMouseDown={inBlend ? dragSelect.onMouseDown(row.key, isSelected) : undefined}
                      onMouseEnter={inBlend ? dragSelect.onMouseEnter(row.key) : undefined}
                      ariaLabel={`Select ${row.missing ? row.key : material.name}`}
                    />
                  </td>

                  <td className="sticky-col-2">
                    {/* The peek trigger leads the cell, so every material name
                        shares a left edge and the add row's plus can line up
                        with it. Icon and name sit in one non-wrapping row: a
                        long name wraps BESIDE the icon instead of dropping
                        under it and leaving the icon alone on a line. */}
                    <div className="blend-material-cell">
                      {row.missing ? (
                        // Nothing to show for a material the book no longer
                        // has, but the space is kept so its name lines up.
                        <span className="blend-peek-btn is-spacer" aria-hidden="true" />
                      ) : (
                        <button
                          type="button"
                          className={`blend-peek-btn${peek.openId === row.key ? ' is-open' : ''}`}
                          ref={peek.anchorRef(row.key)}
                          {...peek.triggerProps(row.key)}
                          aria-label={`Nutritional values for ${material.name}`}
                        >
                          <span className="material-symbols-rounded" aria-hidden="true">info</span>
                        </button>
                      )}
                      <div className="blend-material-text">
                        <span className="blend-material-name">
                          {isLocked && (
                            <span className="material-symbols-rounded blend-lock-mark"
                                  title="Locked — balancing won't move this">lock</span>
                          )}
                          {row.missing ? row.key : material.name}
                        </span>
                        {row.missing && (
                          <span className="blend-row-warning" title="This material is no longer in the master list">
                            not in the material list
                          </span>
                        )}
                        {priceMissing && !row.missing && (
                          <span className="blend-row-warning" title="No price recorded for this material in the selected column">
                            no price
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* A shows 0 where B has the material and A does not — but
                      only while comparing. Outside a comparison a blank cell
                      means "not in this blend", and filling it with 0 would put
                      every material in the book at zero the moment "show every
                      material" was switched on. */}
                  <td className="sticky-col-3">
                    <input
                      type="text"
                      inputMode="decimal"
                      data-grid-cell="true"
                      ref={grid.registerCell(row.key, QTY_COL)}
                      value={raw ?? (comparing && compareValue !== undefined ? '0' : '')}
                      disabled={disabled}
                      onFocus={() => grid.setActive(row.key, QTY_COL)}
                      onChange={(e) => {
                        /*
                         * A signed-off maximum is refused rather than warned
                         * about. Refusing at the keystroke, not on save: a
                         * figure that is going to be rejected should never sit
                         * there looking accepted while the rest of the blend is
                         * balanced around it.
                         *
                         * Only APPROVED limits do this — see utils/blendLimits.
                         * The proposed set warns nobody and blocks nothing
                         * until somebody has signed it off.
                         */
                        const check = checkInclusion(row.key, e.target.value);
                        if (check?.level === 'over') return;
                        draft.setValue(row.key, e.target.value);
                      }}
                      className={`blend-cell-input${inclusionCheck ? ` inclusion-${inclusionCheck.level}` : ''}`}
                      title={inclusionCheck?.message || undefined}
                      onKeyDown={(e) => {
                        // "=" fills this cell with whatever is missing from 100.
                        if (e.key === '=') {
                          e.preventDefault();
                          draft.fillToBalance(row.key);
                        }
                        // "?" shows or hides this row's nutritional values.
                        // The grid keeps Tab for its own cells, so this is the
                        // keyboard's way to the panel. Never a character a
                        // quantity can contain, so nothing is lost to it.
                        if (e.key === '?') {
                          e.preventDefault();
                          if (!row.missing) peek.toggle(row.key, 'cell');
                        }
                      }}
                      // A panel opened with "?" belongs to this cell: moving to
                      // another row (arrows, Enter, a click) puts it away.
                      onBlur={() => peek.closeIfMode('cell')}
                      aria-label={`Inclusion percent for ${row.missing ? row.key : material.name}`}
                      // Focus stays here when "?" opens the panel, so this is
                      // the element a screen reader has to find it from — the
                      // icon's own aria-describedby is no help to it.
                      aria-describedby={peek.openId === row.key ? peek.panelId : undefined}
                    />
                  </td>

                  {comparing && (
                    <td className="sticky-col-3 bg-ref-col num">
                      {/* 0, not a dash: the material IS in this comparison, and
                          "B has none of it" is a figure. A dash reads as "not
                          applicable", which is a different statement. */}
                      {fmt(compareValue === undefined || compareValue === '' ? 0 : compareValue)}
                    </td>
                  )}

                  <td className="sticky-col-4 sticky-col-last actions-col"
                      onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu actions={buildRowActions(row)} triggerTitle="Row actions" />
                  </td>

                  <td className="first-scrollable num">{price === undefined || price === null ? '—' : fmt(price)}</td>
                  <td className="num">{contribution ? fmt(contribution.contribution) : '—'}</td>
                  {comparing && (
                    <td className="num bg-ref-col">
                      {formatCurrency(compareContribution || 0)}
                    </td>
                  )}
                </tr>
              );
            })}

          </tbody>

          <tfoot>
            <tr className={`blend-total-row${Math.abs(total - 100) <= 0.01 ? ' balanced' : ''}`}>
              <td className="sticky-col-1 checkbox-col" />
              <td className="sticky-col-2">Total</td>
              <td className="sticky-col-3 num">{fmt(total)}</td>
              {/* B's totals. Without these the foot ran a column short of the
                  head while comparing, so every figure in it sat under the
                  wrong heading — the £/T total appeared beneath Qty % B. */}
              {comparing && (
                <td className="sticky-col-3 bg-ref-col num">{fmt(compareTotal)}</td>
              )}
              <td className="sticky-col-4 sticky-col-last actions-col" />
              <td className="first-scrollable num" />
              {/* formatCurrency, not fmt — the summary panel renders the same
                  figure through it, and toFixed rounds a half-penny the other
                  way, so the two disagreed by 1p on screen. */}
              <td className="num">
                {summary?.pricing?.[priceColumnKey]
                  ? formatCurrency(summary.pricing[priceColumnKey].rawMaterialCost)
                  : '—'}
              </td>
              {comparing && (
                <td className="num bg-ref-col">{formatCurrency(compareCost)}</td>
              )}
            </tr>
          </tfoot>
        </table>
      </div>

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        bulkActions={buildBulkActions}
        actions={genericActions}
      />

      {/* The nutrition panel. In a portal at a fixed position, so the grid's
          scroll box and its sticky cells have no say in whether it shows.
          Values come from the cost book as priced on the calculator's date
          (GET /api/blends/book?asOf=…, never cached), so they are the same
          figures the blend's own analysis is averaged from. */}
      {peekMaterial && createPortal(
        <div
          key={peek.openId}
          ref={peek.panelRef}
          id={peek.panelId}
          role="tooltip"
          className="blend-peek-tip"
          data-placement={peek.placement}
          style={peek.panelStyle}
        >
          <div className="bpt-head">
            <span className="bpt-name">{peekMaterial.name}</span>
            <span className="bpt-basis">Analysis, fresh basis</span>
          </div>
          {nutrientComponents.length > 0 ? (
            <dl className="bpt-list">
              {nutrientComponents.map((c) => (
                <div className="bpt-row" key={c.key}>
                  <dt>{c.label}</dt>
                  <dd>
                    {peekMaterial.nutrients?.[c.key] === undefined
                      ? '—'
                      : fmt(peekMaterial.nutrients[c.key], c.decimals ?? 2)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="bpt-empty">No nutritional values available.</p>
          )}
          {/* Only for a material in the blend — anything else costs nothing. */}
          {peekContribution?.shareOfCost != null && (
            <div className="bpt-row bpt-cost">
              <span>Share of blend cost</span>
              <span>{fmt(peekContribution.shareOfCost, 1)}%</span>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
